/**
 * Shared sync hook used by SettingsWindow's "Skill Library" tab and
 * the SkillLibraryWindow's title-bar Sync button. Calls lib/graph
 * listSkills, then iterates upsertFromExternal so the Convex cache
 * stays the source of truth (decision D3).
 *
 * J.6 — Auto-resolves siteId/driveId from `tenantBranding` instead of
 * reading VITE_REPANCY_FIRM_SKILLS_* env vars. The Settings + Library
 * surfaces are both mounted inside `/c/${slug}` (via Win95Desktop), so
 * `useOptionalTenantBranding()` always returns the resolved row when
 * the hook fires. Falls back to a quieter "no Firm-Skills site
 * configured" message when the portal's row has empty values (e.g.
 * the `__probe__` portal). See decisions.md row 2026-05-19 J.6.
 */
import { useCallback, useState } from 'react';
import { useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { listSkills } from '../lib/graph/skills';
import { useOptionalTenantBranding } from '../lib/tenant/TenantBrandingContext';

export interface SkillSyncStatus {
  kind: 'idle' | 'syncing' | 'done' | 'error';
  message?: string;
}

export interface UseSkillSyncResult {
  status: SkillSyncStatus;
  sync: () => Promise<void>;
}

/**
 * Pure helper extracted for unit tests. Given a (possibly null)
 * tenantBranding row, returns either the resolved (siteId, driveId)
 * tuple or a user-facing reason string. Reason strings are short
 * sentences; never env-var instructions.
 */
export function resolveSkillSyncTarget(branding: {
  firmSkillsSiteId: string | null | undefined;
  firmSkillsDriveId: string | null | undefined;
} | null): { siteId: string; driveId: string } | { reason: string } {
  if (branding === null) {
    return {
      reason:
        'No portal context — open Settings from a firm portal to sync the Skill library.',
    };
  }
  const siteId =
    typeof branding.firmSkillsSiteId === 'string' &&
    branding.firmSkillsSiteId.length > 0
      ? branding.firmSkillsSiteId
      : null;
  const driveId =
    typeof branding.firmSkillsDriveId === 'string' &&
    branding.firmSkillsDriveId.length > 0
      ? branding.firmSkillsDriveId
      : null;
  if (siteId === null || driveId === null) {
    return {
      reason:
        'This portal has no Firm-Skills SharePoint site configured yet — ask your operator.',
    };
  }
  return { siteId, driveId };
}

export function useSkillSync(): UseSkillSyncResult {
  const convex = useConvex();
  const portal = useOptionalTenantBranding();
  const [status, setStatus] = useState<SkillSyncStatus>({ kind: 'idle' });

  const sync = useCallback(async (): Promise<void> => {
    const resolved = resolveSkillSyncTarget(
      portal === null ? null : portal.branding,
    );
    if ('reason' in resolved) {
      setStatus({ kind: 'error', message: resolved.reason });
      return;
    }
    const { siteId, driveId } = resolved;
    setStatus({ kind: 'syncing' });
    try {
      const { remote, skipped } = await listSkills({ siteId, driveId });
      let upserted = 0;
      let created = 0;
      for (const skill of remote) {
        const result = await convex.mutation(
          api.skills.upsertFromExternal as Parameters<typeof convex.mutation>[0],
          {
            name: skill.frontmatter.name,
            description: skill.frontmatter.description,
            scope: 'firm',
            externalRef: {
              source: 'sharepoint',
              siteId,
              driveId,
              itemId: skill.itemId,
              folderItemId: skill.folderItemId,
              etag: skill.etag,
              webUrl: skill.webUrl,
            },
            body: skill.body,
            frontmatter: skill.frontmatter,
            scriptManifest: skill.manifest,
          },
        );
        upserted += 1;
        if ((result as { created?: boolean })?.created) created += 1;
      }
      if (skipped.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('Skill sync skipped invalid folders:', skipped);
      }
      const skippedNote = skipped.length > 0 ? ` (${skipped.length} skipped)` : '';
      setStatus({
        kind: 'done',
        message: `Synced ${upserted} skill${upserted === 1 ? '' : 's'} (${created} new)${skippedNote}.`,
      });
    } catch (err) {
      setStatus({ kind: 'error', message: (err as Error).message });
    }
  }, [convex, portal]);

  return { status, sync };
}
